/**
 * Tour API functions
 * Public functions for managing tour lifecycle and UI
 */

import { TOUR_STYLES_ID, TOUR_CSS } from './styles'
import type { TourDemoType } from './types'
import { getDemoHTML } from './demos'

/**
 * Inject tour styles into the document
 * Safe to call multiple times - only injects once
 */
export const injectTourStyles = (): void => {
    if (document.getElementById(TOUR_STYLES_ID)) return

    const style = document.createElement('style')
    style.id = TOUR_STYLES_ID
    style.textContent = TOUR_CSS
    document.head.appendChild(style)
}

/**
 * Remove tour styles from the document
 */
export const removeTourStyles = (): void => {
    document.getElementById(TOUR_STYLES_ID)?.remove()
}

// Store keyboard handler reference for cleanup
let currentKeyboardHandler: ((e: KeyboardEvent) => void) | null = null

/**
 * Create tour overlay element
 */
export const createTourOverlay = (): void => {
    if (document.querySelector('.tour-overlay')) return

    const overlay = document.createElement('div')
    overlay.className = 'tour-overlay'
    document.body.appendChild(overlay)
}

/**
 * Cleanup all tour elements from the DOM
 */
export const cleanupTourElements = (): void => {
    // Remove keyboard handler
    if (currentKeyboardHandler) {
        document.removeEventListener('keydown', currentKeyboardHandler)
        currentKeyboardHandler = null
    }

    document
        .querySelector('.tour-highlight-element')
        ?.classList.remove('tour-highlight-element')
    document.querySelector('.tour-popover-container')?.remove()
    document.querySelector('.tour-overlay')?.remove()
}

/**
 * Create tour popover element with content
 */
export const createTourPopover = (options: {
    title: string
    description: string
    currentStep: number
    totalSteps: number
    onPrev?: () => void
    onNext: () => void
    onClose: () => void
    isLastStep?: boolean
    demo?: TourDemoType
}): HTMLDivElement => {
    const {
        title,
        description,
        currentStep,
        totalSteps,
        onPrev,
        onNext,
        onClose,
        isLastStep,
        demo,
    } = options

    const demoHTML = getDemoHTML(demo)

    const popover = document.createElement('div')
    popover.className = 'tour-popover-container'
    popover.innerHTML = `
        <button class="tour-close" aria-label="Close tour">×</button>
        <h3>${title}</h3>
        <p>${description}</p>
        ${demoHTML}
        <div class="tour-footer">
            <div>
                <span class="tour-progress">${currentStep} / ${totalSteps}</span>
                <div class="tour-keyboard-hint"><kbd>Enter</kbd> hoặc <kbd>→</kbd> để tiếp tục</div>
            </div>
            <div class="tour-footer-buttons">
                ${
                    currentStep > 1
                        ? '<button class="tour-btn tour-btn-prev">Trước</button>'
                        : ''
                }
                <button class="tour-btn tour-btn-next">${
                    isLastStep ? 'Hoàn thành' : 'Tiếp theo'
                }</button>
            </div>
        </div>
    `

    // Attach click event listeners
    popover.querySelector('.tour-close')?.addEventListener('click', onClose)
    popover
        .querySelector('.tour-btn-prev')
        ?.addEventListener('click', () => onPrev?.())
    popover.querySelector('.tour-btn-next')?.addEventListener('click', onNext)

    // Keyboard navigation: Enter/Tab = next, Shift+Tab = prev, Escape = close
    if (currentKeyboardHandler) {
        document.removeEventListener('keydown', currentKeyboardHandler)
    }

    currentKeyboardHandler = (e: KeyboardEvent) => {
        // Ignore if typing in an input field
        const target = e.target as HTMLElement
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            return
        }

        switch (e.key) {
            case 'Enter':
            case 'Tab':
                if (e.shiftKey && e.key === 'Tab') {
                    // Shift+Tab = previous step
                    e.preventDefault()
                    onPrev?.()
                } else {
                    // Enter or Tab = next step
                    e.preventDefault()
                    onNext()
                }
                break
            case 'Escape':
                e.preventDefault()
                onClose()
                break
            case 'ArrowRight':
                e.preventDefault()
                onNext()
                break
            case 'ArrowLeft':
                e.preventDefault()
                onPrev?.()
                break
        }
    }

    document.addEventListener('keydown', currentKeyboardHandler)

    return popover
}

/**
 * Position popover relative to target element
 */
export const positionPopover = (
    popover: HTMLElement,
    targetElement: HTMLElement
): void => {
    const rect = targetElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Get actual popover dimensions after it's rendered (important for mobile responsiveness)
    const popoverRect = popover.getBoundingClientRect()
    const popoverHeight = popoverRect.height || 200
    const popoverWidth = popoverRect.width || Math.min(380, viewportWidth - 20)

    const spaceBelow = viewportHeight - rect.bottom
    const showAbove = spaceBelow < popoverHeight + 20 && rect.top > spaceBelow

    let popoverTop = showAbove
        ? rect.top - popoverHeight - 20
        : rect.bottom + 20
    let popoverLeft = rect.left + rect.width / 2 - popoverWidth / 2

    // On mobile, center horizontally with padding
    if (viewportWidth < 768) {
        popoverLeft = (viewportWidth - popoverWidth) / 2
    }

    popoverLeft = Math.max(
        10,
        Math.min(popoverLeft, viewportWidth - popoverWidth - 10)
    )

    // Ensure popover is within viewport
    if (popoverTop < 10) popoverTop = 10
    if (popoverTop + popoverHeight > viewportHeight - 10) {
        popoverTop = viewportHeight - popoverHeight - 10
    }

    popover.style.top = `${popoverTop}px`
    popover.style.left = `${popoverLeft}px`
}
